'use client';
import React, { useState } from 'react';

type FormFields = {
  inquiryType: string;
  information: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  location: string;
  area: string;
  property: string;
  budget: string;
  message: string;
  agree: boolean;
};

type Errors = Partial<Record<keyof FormFields, string>>;

const INITIAL: FormFields = {
  inquiryType: '',
  information: '',
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  location: '',
  area: '',
  property: '',
  budget: '',
  message: '',
  agree: false,
};

function validateField(name: keyof FormFields, value: string | boolean): string {
  switch (name) {
    case 'firstName':
      return !String(value).trim() ? 'First name is required' : '';
    case 'lastName':
      return !String(value).trim() ? 'Last name is required' : '';
    case 'email':
      if (!String(value).trim()) return 'Email address is required';
      if (!/\S+@\S+\.\S+/.test(String(value))) return 'Enter a valid email address';
      return '';
    case 'mobile':
      if (!String(value).trim()) return 'Mobile number is required';
      if (!/^[\d\s\+\-\(\)]{7,}$/.test(String(value))) return 'Enter a valid phone number';
      return '';
    case 'agree':
      return !value ? 'You must accept the Privacy Policy to continue' : '';
    default:
      return '';
  }
}

// Field wrapper with label, input/select, and inline error
function Field({
  label,
  error,
  children,
}: {
  label?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      {label && (
        <label className="block text-[12px] font-semibold text-[#50514f] mb-1 uppercase">
          {label}
        </label>
      )}
      {children}
      {error && (
        <p className="flex items-center gap-1 text-[11px] text-red-500 font-semibold mt-1 animate-in slide-in-from-top-1 duration-200">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="shrink-0 mt-px">
            <circle cx="5" cy="5" r="5" opacity="0.15" />
            <text x="5" y="7.5" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor">!</text>
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}

export default function ContactForm() {
  const [formData, setFormData] = useState(INITIAL);
  const [errors, setErrors] = useState({} as Errors);
  const [touched, setTouched] = useState({} as Partial<Record<keyof FormFields, boolean>>);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Mark field as touched and validate immediately
  const handleBlur = (name: keyof FormFields) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, formData[name]);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const fieldName = name as keyof FormFields;
    const fieldValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));

    // Validate inline only if field was already touched
    if (touched[fieldName]) {
      const err = validateField(fieldName, fieldValue);
      setErrors((prev) => ({ ...prev, [fieldName]: err }));
    }
  };

  const validateAll = (): Errors => {
    const required: (keyof FormFields)[] = ['firstName', 'lastName', 'email', 'mobile', 'agree'];
    const newErrors: Errors = {};
    const newTouched: Partial<Record<keyof FormFields, boolean>> = {};
    required.forEach((field) => {
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
      newTouched[field] = true;
    });
    setTouched((prev) => ({ ...prev, ...newTouched }));
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateAll();
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'contact',
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.mobile,
          details: {
            inquiryType: formData.inquiryType,
            information: formData.information,
            location: formData.location,
            area: formData.area,
            property: formData.property,
            budget: formData.budget,
            message: formData.message,
          },
        }),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (name: keyof FormFields) =>
    `w-full border rounded px-3 py-2.5 text-[13px] text-gray-700 focus:outline-none transition-colors placeholder-gray-400 ${
      errors[name] && touched[name]
        ? 'border-red-400 bg-red-50/30 focus:border-red-500'
        : touched[name] && !errors[name]
        ? 'border-green-400 bg-green-50/20 focus:border-[#017743]'
        : 'border-gray-200 focus:border-[#017743]'
    }`;

  const selectClass = (name?: keyof FormFields) =>
    `w-full border rounded px-3 py-2.5 text-[13px] text-gray-600 focus:outline-none focus:border-[#017743] appearance-none transition-colors ${
      name && errors[name] && touched[name] ? 'border-red-400 bg-red-50/30' : 'border-gray-200'
    }`;

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{
        backgroundImage: 'url(/uploads/Employee-Training-Website-6-scaled.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h2
              className="text-[48px] lg:text-[56px] font-normal leading-tight text-[#1a1a1a] mb-6"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              Connect With Our Land Experts
            </h2>
            <div className="w-24 h-0.5 bg-[#50514f] mb-6" />
            <p className="text-[15px] text-[#50514f] leading-relaxed">
              <strong>Ready to explore the right plot for your next project?</strong> Speak with our
              specialist team today for personalized guidance, verified listings, and seamless
              support every step of the way.
            </p>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            {submitted ? (
              <div className="py-10 text-center space-y-4">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto text-[#006838] text-3xl border border-green-100">
                  ✓
                </div>
                <h3
                  className="text-[18px] font-bold text-[#1a1a1a]"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  Thank You!
                </h3>
                <p className="text-[13px] text-[#50514f]">
                  Your inquiry has been received. Our land experts will contact you within 2 hours.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setFormData(INITIAL); setErrors({}); setTouched({}); }}
                  className="text-[12px] font-semibold text-[#017743] hover:underline"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {/* Row 1: Inquiry Type & Information */}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Inquiry Type">
                    <select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      className={selectClass()}
                    >
                      <option value="">Select</option>
                      <option value="buy">Buy</option>
                      <option value="sell">Sell</option>
                    </select>
                  </Field>
                  <Field label="Information">
                    <select
                      name="information"
                      value={formData.information}
                      onChange={handleChange}
                      className={selectClass()}
                    >
                      <option value="">I&apos;m a</option>
                      <option value="agent">I&apos;m a real estate agent</option>
                      <option value="buyer">I&apos;m a buyer</option>
                      <option value="owner">I&apos;m a property owner</option>
                      <option value="referral">I&apos;m a Referral</option>
                    </select>
                  </Field>
                </div>

                {/* Row 2: First Name & Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <Field error={touched.firstName ? errors.firstName : undefined}>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="* First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={() => handleBlur('firstName')}
                      className={inputClass('firstName')}
                    />
                  </Field>
                  <Field error={touched.lastName ? errors.lastName : undefined}>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="* Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={() => handleBlur('lastName')}
                      className={inputClass('lastName')}
                    />
                  </Field>
                </div>

                {/* Row 3: Email & Mobile */}
                <div className="grid grid-cols-2 gap-4">
                  <Field error={touched.email ? errors.email : undefined}>
                    <input
                      type="email"
                      name="email"
                      placeholder="* Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur('email')}
                      className={inputClass('email')}
                    />
                  </Field>
                  <Field error={touched.mobile ? errors.mobile : undefined}>
                    <input
                      type="tel"
                      name="mobile"
                      placeholder="* Mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      onBlur={() => handleBlur('mobile')}
                      className={inputClass('mobile')}
                    />
                  </Field>
                </div>

                {/* Row 4: Location & Area */}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Location">
                    <select
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className={selectClass()}
                    >
                      <option value="">Select City</option>
                      <option value="abu-dhabi">Abu Dhabi</option>
                      <option value="ajman">Ajman</option>
                      <option value="dubai">Dubai</option>
                      <option value="fujairah">Fujairah</option>
                      <option value="rak">Ras Al Khaimah</option>
                      <option value="sharjah">Sharjah</option>
                      <option value="uaq">Umm Al Quwain</option>
                    </select>
                  </Field>
                  <Field label="Area">
                    <select
                      name="area"
                      value={formData.area}
                      onChange={handleChange}
                      className={selectClass()}
                    >
                      <option value="">Select Area</option>
                      <option value="al-marjan">Al Marjan Island</option>
                      <option value="bu-kadra">BU KADRA-MEYDAN</option>
                      <option value="business-bay">BUSINESS BAY</option>
                      <option value="downtown">DOWNTOWN</option>
                      <option value="dubai-island">DUBAI ISLAND</option>
                      <option value="dubai-land">DUBAI LAND</option>
                      <option value="jvc">JVC</option>
                      <option value="majan">MAJAN</option>
                      <option value="pearl-jumeirah">Pearl Jumeirah</option>
                      <option value="sports-city">SPORTS CITY</option>
                    </select>
                  </Field>
                </div>

                {/* Row 5: Property & Budget */}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Property">
                    <select
                      name="property"
                      value={formData.property}
                      onChange={handleChange}
                      className={selectClass()}
                    >
                      <option value="">Select type</option>
                      <option value="residential">Residential Plot</option>
                      <option value="commercial">Commercial Plot</option>
                      <option value="industrial">Industrial Plot</option>
                    </select>
                  </Field>
                  <Field label="Budget Range">
                    <input
                      type="text"
                      name="budget"
                      placeholder="Your Budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded px-3 py-2.5 text-[13px] text-gray-600 focus:outline-none focus:border-[#017743] placeholder-gray-400 transition-colors"
                    />
                  </Field>
                </div>

                {/* Message */}
                <Field label="Message">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-200 rounded px-3 py-2.5 text-[13px] text-gray-600 focus:outline-none focus:border-[#017743] resize-none transition-colors"
                  />
                </Field>

                {/* Checkbox */}
                <div className="space-y-1">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      name="agree"
                      id="agree-contact"
                      checked={formData.agree}
                      onChange={handleChange}
                      onBlur={() => handleBlur('agree')}
                      className="mt-0.5 w-4 h-4 accent-[#017743] shrink-0"
                    />
                    <label htmlFor="agree-contact" className="text-[12px] text-[#50514f] cursor-pointer leading-relaxed">
                      I agree to be contacted by Plotae.com and accept the Privacy Policy.
                    </label>
                  </div>
                  {touched.agree && errors.agree && (
                    <p className="flex items-center gap-1 text-[11px] text-red-500 font-semibold ml-6 animate-in slide-in-from-top-1 duration-200">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="shrink-0">
                        <circle cx="5" cy="5" r="5" opacity="0.15" />
                        <text x="5" y="7.5" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor">!</text>
                      </svg>
                      {errors.agree}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#017743] text-white rounded py-3 text-[14px] font-semibold hover:bg-[#015c33] transition-colors duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
